import "moment/locale/fr";
import Header from "components/layout/header/Header";
import PageContext from "pages/PageContext";
import _ from "lodash";
import moment from "moment";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useLocation } from "react-router-dom";
import { usePersonContext } from "api/directory/person/PersonProvider";
import { useUserContext } from "api/user/UserProvider";
import { useAlertsContext } from "api/alerts/AlertsProvider";
import { phoneSize } from "utils/screenSizes";
import { useStaffContext } from "api/directory/staff/StaffProvider";
import { useBranchesContext } from "api/settings/branches/BranchesProvider";
import { FILTERS_FOR_NAMES } from "constants/constants";
import { canUserPerformAction } from "access-roles/general-functions";
import { STAFF_ROLES } from "access-roles/roles";
import FafLateDeclarationPopup from "components/modal/FafLateDeclarationPopup";

const Page = ({ children }) => {
  const {
    lang,
    userSetLang,
    user,
    userSetScope,
    userScope,
    setUserScope,
    logout,
    backInitialAccount,
    connectedUser,
    userScopeList,
    userRoleList,
  } = useUserContext();

  const { unreadNotifications } = useAlertsContext();

  const { hierarchyEmailAddresses, getHierarchyEmailAddresses } =
    usePersonContext();
  const { staffById, getStaffById } = useStaffContext();
  const { branches, fetchBranches } = useBranchesContext();
  moment.locale(lang);
  const { searchGeneralByProfiles, usersByProfiles, getNamesByFilters } =
    useGeneralContext();
  const [fetchedUserData, setFetchedUserData] = useState(false);
  const [slugSearched, setSlugSearched] = useState("");
  const [isLinkClick, setIsLinkClick] = useState(false);
  const [isNotificationsPage, setIsNotificationsPage] = useState(false);
  const [customRedirection, setCustomRedirection] = useState("");
  const [connectedUserGp, setConnectedUserGp] = useState(null);

  const location = useLocation();

  const searchUser = useCallback(
    ({ search, profile, isPartnerConnection = false }) => {
      if (search.length > 2 && !_.isNil(userScope)) {
        searchGeneralByProfiles({ search, profile, isPartnerConnection });
      }
    },
    [searchGeneralByProfiles, userScope]
  );

  useEffect(() => {
    // Wait for userRoleList to be populated before fetching
    if (fetchedUserData || !userScope || !userRoleList?.length) return;
    const id = localStorage.getItem("id");
    if (!id) return;

    const hasPermission = canUserPerformAction({
      requiredRoles: [
        STAFF_ROLES.ROLE_GET_STAFF,
        STAFF_ROLES.ROLE_READ_STAFF,
        STAFF_ROLES.ROLE_MACRO_AS_READ_STAFF,
      ],
      userRoles: userRoleList,
    });

    if (!hasPermission) return;

    let isCancelled = false;

    const fetchData = async () => {
      try {
        const targetId =
          staffById?.id !== connectedUser.id ? connectedUser?.id : id;
        await getStaffById(targetId);
        await fetchBranches();
        if (!isCancelled) {
          setFetchedUserData(true);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error fetching user data:", error);
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [fetchedUserData, userScope, userRoleList]);

  useEffect(() => {
    const fetchGp = async () => {
      if (staffById?.gp_id && !connectedUserGp) {
        const result = await getNamesByFilters({
          id: [staffById?.gp_id],
          types: [FILTERS_FOR_NAMES.PERSON],
        });
        if (result?.[0]) {
          setConnectedUserGp(result[0]);
        }
      }
    };
    fetchGp();
  }, [staffById?.gp_id]);

  useEffect(() => {
    if (
      !_.isEmpty(staffById) &&
      !["/login", "/logout", "/authorize"].includes(location.pathname)
    ) {
      const persist = (key, value) => {
        if (value == null) localStorage.removeItem(key);
        else localStorage.setItem(key, value);
      };
      persist("picture_url", staffById?.picture_url);
      persist("first_name", staffById?.first_name);
      persist("last_name", staffById?.last_name);
    }
    setIsNotificationsPage(location.pathname.includes("notifications"));
  }, [staffById, location.pathname, userScope]);

  const connectedUserBranch =
    branches?.length && branches.find((b) => b.id === staffById?.branch_id);

  const connectedUserFormData = useMemo(
    () => ({
      branch: connectedUserBranch
        ? [{ value: connectedUserBranch.id, label: connectedUserBranch.name }]
        : [],
      manager:
        staffById?.id && staffById?.first_name
          ? [
              {
                value: staffById.id,
                label: `${staffById.first_name} ${staffById.last_name}`,
              },
            ]
          : [],
      gp: connectedUserGp
        ? [
            {
              value: connectedUserGp.id,
              label: `${connectedUserGp.first_name} ${connectedUserGp.last_name}`,
            },
          ]
        : [],
      role: staffById?.role || [],
    }),
    [connectedUserBranch, staffById, connectedUserGp]
  );

  const values = useMemo(
    () => ({
      customRedirection,
      setCustomRedirection,
      backInitialAccount: backInitialAccount,
      lang: lang,
      logout: logout,
      search: searchUser,
      searchResult: usersByProfiles || [],
      userScopeList: userScopeList,
      connectedUser: staffById || {},
      user: user,
      userScope: userScope,
      userSetLang: userSetLang,
      userSetScope: userSetScope,
      setUserScope: setUserScope,
      slugSearched: slugSearched,
      setSlugSearched: setSlugSearched,
      isLinkClick,
      setIsLinkClick,
      getHierarchyEmailAddresses,
      hierarchyEmailAddresses,
      unreadNotifications,
      connectedUserInfos: staffById || {},
      connectedUserFormData,
    }),
    [
      customRedirection,
      setCustomRedirection,
      setSlugSearched,
      slugSearched,
      backInitialAccount,
      lang,
      logout,
      searchUser,
      usersByProfiles,
      userScopeList,
      staffById,
      user,
      userScope,
      userSetLang,
      setUserScope,
      userSetScope,
      isLinkClick,
      setIsLinkClick,
      getHierarchyEmailAddresses,
      hierarchyEmailAddresses,
      unreadNotifications,
      connectedUserFormData,
    ]
  );

  return ["/login", "/logout", "/authorize"].includes(location.pathname) ||
    location.pathname.startsWith("/balancesheet") ||
    location.pathname.startsWith("/davidson-evaluation/mission") ||
    location.pathname.startsWith("/davidson-evaluation/davidson/consultant") ? (
    <>{children}</>
  ) : location.pathname.includes(
      "/evaluation/professional-evaluation/show/consultant"
    ) ? (
    <>
      <PageContext.Provider value={values}>
        <Header />
        {children}
      </PageContext.Provider>
      <FafLateDeclarationPopup />
    </>
  ) : (
    <>
      <PageContext.Provider value={values}>
        <Header />
        <div
          className={
            !phoneSize
              ? "page-container"
              : !isNotificationsPage && "page-container"
          }
        >
          {children}
        </div>
      </PageContext.Provider>
      <FafLateDeclarationPopup />
    </>
  );
};

export default Page;
